// Richieste di prenotazione che arrivano da RVC dal ponte (messaggio `spa-prenotazione`, docs/PONTE.md) e
// risposte della SPA (messaggio `spa-stato`). Logica pura: si prova da sola (tests/ponte-app.test.js).
// Una richiesta = un documento `ponte/<id di RVC>`. Mai dati di salute: il questionario si compila in SPA.
import { TRATTAMENTI } from './listino.js';
import { START, END, STEP, SALE, OPERATRICI, TIPI, toMin, hhmm, inTurno, primoLibero } from './agenda.js';

export const AZIONI = ['nuova', 'modifica', 'annulla'];
// Stati della richiesta nella SPA. «arrivata» non è uno stato qui: è la prima risposta che va a RVC da sola.
export const STATI_RICHIESTA = ['da-rispondere', 'confermata', 'altro-orario', 'rifiutata', 'annullata'];
export const RISPOSTE = ['arrivata', 'confermata', 'altro-orario', 'rifiutata'];

const DATA = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const ORA = /^([01]\d|2[0-3]):[0-5]\d$/;
const pulisci = (s, max) => String(s ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const facoltativo = (s, max) => pulisci(s, max) || null;

/**
 * Controlla i dati di un `spa-prenotazione` di RVC. Il formato è quello di RVC (docs/APP.md di RVC, «C60 Spa»).
 * @returns {string|null} codice del problema (la richiesta si salva lo stesso: si può solo rifiutare) o null
 */
export function problemaArrivo(dati, indice = TRATTAMENTI) {
  const d = dati || {};
  if (!AZIONI.includes(d.azione)) return 'azione';
  if (!DATA.test(String(d.data || ''))) return 'data';
  if (!ORA.test(String(d.inizio || ''))) return 'ora';
  const tr = indice[d.tr];
  if (!tr) return 'trattamento';
  if (!(d.persone === 1 || d.persone === 2) || (tr.solo && d.persone === 2)) return 'persone';
  if (!d.camera && !d.esterno?.nome) return 'cliente';
  if (!TIPI.includes(d.tipo || 'pagamento')) return 'tipo';
  return null;
}

/**
 * Documento della richiesta da salvare quando arriva un messaggio di RVC (o null se è vecchio o già salvato).
 * Senza i campi del server (arrivata, aggiornato, ultimoEvento): li aggiunge chi scrive.
 * @param {object} m messaggio del ponte { id, versione, nome, dati }
 * @param {object|null} prima documento già salvato
 */
export function docArrivo(m, prima = null, indice = TRATTAMENTI) {
  const versione = Number.isInteger(m?.versione) ? m.versione : 1;
  if (prima && prima.versione >= versione) return null;
  const d = m.dati || {};
  const errore = problemaArrivo(d, indice);
  const annulla = d.azione === 'annulla';
  const esterno = d.esterno?.nome
    ? { nome: pulisci(d.esterno.nome, 60), tel: facoltativo(d.esterno.tel, 30), email: facoltativo(d.esterno.email, 80) }
    : null;
  const doc = {
    da: 'rvc',
    tipo: 'spa-prenotazione',
    versione,
    azione: AZIONI.includes(d.azione) ? d.azione : 'nuova',
    data: DATA.test(String(d.data || '')) ? d.data : '2000-01-01',
    inizio: ORA.test(String(d.inizio || '')) ? d.inizio : '00:00',
    tr: pulisci(d.tr, 40),
    durata: indice[d.tr]?.durata ?? (Number.isInteger(d.durata) && d.durata >= 0 && d.durata <= 600 ? d.durata : 0),
    persone: d.persone === 2 ? 2 : 1,
    camera: esterno ? null : facoltativo(d.camera, 10),
    esterno,
    nome: esterno ? esterno.nome : facoltativo(d.nome, 60),
    pagamento: TIPI.includes(d.tipo) ? d.tipo : 'pagamento',
    nota: pulisci(d.nota, 300),
    daNome: pulisci(m.nome, 40),
    errore,
    // Annullata da RVC: si chiude. Nuova o cambiata: va (ri)vista dalla SPA.
    stato: annulla ? 'annullata' : 'da-rispondere',
    proposta: null,
    prenotazioni: prima?.prenotazioni || null,
    // Cosa c'era prima di una modifica (solo per mostrarlo: «prima 16:00»).
    prima: prima && !annulla ? { data: prima.data, inizio: prima.inizio, persone: prima.persone } : null,
    // Prima risposta automatica: «arrivata» (non per l'annullamento: a RVC resta «annullata»).
    invio: annulla && prima?.invio
      ? prima.invio
      : { n: (prima?.invio?.n || 0) + 1, stato: 'arrivata', proposta: null, versione, mandato: false },
  };
  return doc;
}

/**
 * Risposta della SPA: cambia lo stato e prepara il messaggio da mandare (n+1: ogni risposta ha il suo numero).
 * @param {'confermata'|'altro-orario'|'rifiutata'} stato
 * @param {{ data: string, inizio: string } | null} proposta solo con «altro-orario»
 */
export function risposta(r, stato, proposta = null, prenotazioni = null) {
  const p = stato === 'altro-orario' && proposta ? { data: proposta.data, inizio: proposta.inizio } : null;
  return {
    stato,
    proposta: p,
    ...(prenotazioni ? { prenotazioni } : {}),
    invio: { n: (r.invio?.n || 0) + 1, stato, proposta: p, versione: r.versione, mandato: false },
  };
}

/** Messaggio `spa-stato` per RVC della risposta ancora da mandare (stesso id se si rimanda: niente doppioni). */
export function messaggioRisposta(id, r, { nome = '', quando = new Date() } = {}) {
  const i = r.invio;
  return {
    id: `${id}-${i.n}`,
    tipo: 'spa-stato',
    a: 'rvc',
    rif: id,
    versione: 1,
    quando: quando.toISOString(),
    nome: pulisci(nome, 40) || null,
    dati: { stato: i.stato, versione: i.versione, ...(i.proposta ? { proposta: i.proposta } : {}) },
  };
}

/** Prenotazioni candidate per la richiesta (1 o 2 persone): ora chiesta, sala solita del trattamento, prime operatrici in turno. */
export function candidatiRichiesta(r, giorno, indice = TRATTAMENTI) {
  const tr = indice[r.tr];
  const ops = OPERATRICI.filter((o) => inTurno(giorno, o));
  const base = { data: r.data, inizio: r.inizio, durata: tr?.durata || r.durata, tr: r.tr, sala: tr?.sala || SALE[0].id };
  const cs = [{ ...base, lettino: 1, op: ops[0] || OPERATRICI[0] }];
  if (r.persone === 2) cs.push({ ...base, lettino: 2, op: ops[1] || OPERATRICI[1] });
  return cs;
}

/**
 * Controlli automatici della richiesta sul giorno chiesto: c'è posto all'ora chiesta? Se no, il primo orario libero
 * (prima dopo l'ora chiesta, poi da inizio giornata). Mai nel passato.
 * @param {object} giorno dati del giorno (prenotazioni, turni, pause, blocchi) del giorno della richiesta
 * @param {{ data: string, minuti: number }} adesso ora di Roma
 * @param {{ ignora?: string[] }} [opz] prenotazioni da non contare (quelle già legate alla richiesta, se si sposta)
 * @returns {{ esito: 'libero'|'proposta'|'pieno'|'passata'|'errore', posto?: object[], proposta?: object[] }}
 */
export function controllaRichiesta(r, giorno, adesso, { indice = TRATTAMENTI, ignora = r.prenotazioni || [] } = {}) {
  if (r.errore) return { esito: 'errore' };
  const chiesto = toMin(r.inizio);
  const oggi = adesso?.data === r.data;
  if (r.data < adesso.data || (oggi && chiesto < adesso.minuti)) {
    const da = oggi ? adesso : null;
    const p = r.data < adesso.data ? [] : primoLibero(candidatiRichiesta(r, giorno, indice), giorno, { adesso: da, ignora });
    return p?.length ? { esito: 'passata', proposta: p } : { esito: 'passata' };
  }
  const cs = candidatiRichiesta(r, giorno, indice);
  const dopo = primoLibero(cs, giorno, { adesso: { data: r.data, minuti: chiesto }, ignora });
  if (dopo?.length && dopo[0].inizio === r.inizio) return { esito: 'libero', posto: dopo };
  if (dopo?.length) return { esito: 'proposta', proposta: dopo };
  const prima = primoLibero(cs, giorno, { adesso: oggi ? adesso : null, ignora });
  if (prima?.length && toMin(prima[0].inizio) < chiesto) return { esito: 'proposta', proposta: prima };
  return { esito: 'pieno' };
}

/** Orari validi della SPA per una proposta a mano (dall'apertura all'ultimo inizio possibile per quella durata). */
export function orariProposta(durata) {
  const out = [];
  for (let m = START; m + (durata || STEP) <= END; m += STEP) out.push(hhmm(m));
  return out;
}

/** Le prenotazioni legate alla richiesta sono già al giorno, all'ora e con le persone chieste (e non disdette). */
export function prenotazioniUguali(r, legate) {
  const vive = (legate || []).filter((b) => b && b.stato !== 'disdetta' && b.stato !== 'no-show');
  return vive.length === r.persone && vive.every((b) => b.data === r.data && b.inizio === r.inizio);
}

/** Richieste in ordine: prima quelle da rispondere (per giorno e ora), poi le altre (le più recenti in alto). */
export function ordinaRichieste(lista) {
  const daFare = lista.filter((r) => r.stato === 'da-rispondere')
    .sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio));
  const altre = lista.filter((r) => r.stato !== 'da-rispondere')
    .sort((a, b) => (b.data + b.inizio).localeCompare(a.data + a.inizio));
  return { daFare, altre };
}
